// src/pages/AttendanceOverview/components/AttendanceDetailsTab.js
import React, { useEffect, useState } from "react";
import { Card, Table, Row, Col, Button, Spin, Space, Tooltip, Grid } from "antd";

const { useBreakpoint } = Grid;
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAttendanceReport,
  fetchAllEmployees,
} from "../attendanceOverviewThunk";
import {
  selectAttendanceReport,
  selectEmployees,
} from "../attendanceOverviewSelectors";
import dayjs from "dayjs";
import RecordDateFilter from "../../../components/filters/RecordDateFilter";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { BACKEND_API_URL } from "../../../environment";
import { accessChecker, formatToCompanyTimezone } from "../../../lib/functions";
import "./attendanceDetails.css";
import CommonDateRangeFilter from "../../../components/common/filters/CommonDateRangeFilter";

const AttendanceDetailsTab = () => {
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const attendanceReport = useSelector(selectAttendanceReport) || [];

  const employees = useSelector(selectEmployees);
  const authInfo = useSelector((state) => state.auth.auth_info);

  const [filters, setFilters] = useState({
    fromDate: dayjs().startOf("month").format("YYYY-MM-DD"),
    toDate: dayjs().endOf("month").format("YYYY-MM-DD"),
    employee_id: null,
  });
  const [range, setRange] = useState(null);
  const [loadingOverlay, setLoadingOverlay] = useState(false);
  const [pagination, setPagination] = useState({    current: 1,    pageSize: 10,  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  useEffect(() => {
    dispatch(fetchAttendanceReport(filters));
  }, [dispatch, filters]);
  useEffect(() => {
    dispatch(fetchAllEmployees());
  }, [dispatch]);
  

  const LoaderOverlay = () =>
    loadingOverlay ? (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}
      >
        <Spin size="large" />
      </div>
    ) : null;

  const formatText = (value) => {
    if (!value) return "";
    return value
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const columns = [
    { title: "No.", render: (_, __, i) => i + 1,},
    {
      title: "Employee Name",
      dataIndex: "employee_name",
      key: "employee_name",
    },
    {
      title: "Attendance Date",
      dataIndex: "attendance_date",
      key: "attendance_date",

      defaultSortOrder: "descend",

      sorter: (a, b) =>
        new Date(a.attendance_date) - new Date(b.attendance_date),

      render: (v) => (v ? dayjs(v).format("YYYY-MMM-DD") : "-"),
    },
    {
      title: "Attendance Status",
      dataIndex: "attendance",
      key: "attendance",
      render: (value, record) => {
        // const formattedRoles = value
        //   ? value
        //       .split(",")
        //       .map((role) =>
        //         formatText(
        //           role.includes("_") ? role.split("_").slice(1).join(" ") : role
        //         )
        //       )
        //       .join(", ")
        //   : "-";
          let formattedRoles = value
            ? value
                .split(",")
                .map((role) => formatText(role))
                .join(", ")
            : "-";
          // if (record.work_mode === "remote") {
          //   formattedRoles += " (WFH)";
          // }

        return (
          <span
            style={{ color: "#0265DC", fontWeight: "500", fontSize: "12px" }}
          >
            {formattedRoles}
            {record.work_mode === "remote" && (
              <span style={{
                marginLeft: 6,
                fontSize: 11,
                background: "#E3F2FD",
                padding: "2px 6px",
                borderRadius: 8,
                color: "#1565C0"
              }}>
                WFH/Remote
              </span>
            )}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => {
        const displayValueMap = {
          approved: "Approved",
          pending: "Pending",
          rejected: "Rejected",
        };
        const displayValue = displayValueMap[value] || formatText(value);

        let backgroundColor = "transparent";
        let color = "black";
        let dotColor = "black";

        if (value === "pending") {
          backgroundColor = "#EDEEFF";
          color = "#5258E4";
          dotColor = "#5258E4";
        } else if (value === "approved") {
          backgroundColor = "#CEF8E0";
          color = "#007A7D";
          dotColor = "#007A7D";
        } else if (value === "rejected") {
          backgroundColor = "#FFEBE7";
          color = "#D31510";
          dotColor = "#D31510";
        }

        return (
          <div
            style={{
              backgroundColor,
              color,
              display: "flex",
              alignItems: "center",
              gap: "6px",
              borderRadius: "12px",
              padding: "2px 8px",
              fontWeight: "500",
              width: "fit-content",
            }}
          >
            <div
              style={{
                backgroundColor: dotColor,
                borderRadius: "50%",
                width: "6px",
                height: "6px",
              }}
            />
            {displayValue}
          </div>
        );
      },
    },
    { title: "Check-in", dataIndex: "check_in", key: "check_in" , render: (value) =>
    value
      ? formatToCompanyTimezone(value)
      : "-",},
    { title: "Check-out", dataIndex: "check_out", key: "check_out" , render: (value) =>
    value
      ? formatToCompanyTimezone(value)
      : "-",},
    { title: "Shift Start Time", dataIndex: "shift_start", key: "shift_start" },
    { title: "Shift End Time", dataIndex: "shift_end", key: "shift_end" },
    {
      title: "Check In Status",
      dataIndex: "check_in_status",
      key: "check_in_status",
    },
    {
      title: "Check Out Status",
      dataIndex: "check_out_status",
      key: "check_out_status",
    },
  ];

  // const handleFilter = () => {
  //   const payload = {};
  //   if (range && range[0] && range[1]) {
  //     payload.fromDate = range[0].format("YYYY-MM-DD");
  //     payload.toDate = range[1].format("YYYY-MM-DD");
  //   }
  // };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const attendanceReportDownload = async () => {
    try {
      setLoadingOverlay(true);
      const response = await fetch(
        `${BACKEND_API_URL}/hrpayroll/attendance-report-download`,
        {
          method: "POST",
          headers: {
            "auth-token": authInfo.token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            institution_id: authInfo.id,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
            employee_id: accessChecker("approve_attendance")
              ? filters.employee_id
              : authInfo.employee_id,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download PDF");
      }

      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "attendance_report.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download failed", error);
    } finally {
      setLoadingOverlay(false);
    }
  };

  const downloadAttendanceExcel = async () => {
    try {
      setLoadingOverlay(true);
      const response = await fetch(
        `${BACKEND_API_URL}/hrpayroll/attendance-report-excel-download`,
        {
          method: "POST",
          headers: {
            "auth-token": authInfo.token,
            "Content-Type": "application/json",
          },
              body: JSON.stringify({
            institution_id: authInfo.id,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
            employee_id: accessChecker("approve_attendance")
              ? filters.employee_id
              : authInfo.employee_id,
          }),
        }
      );

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "demo_report.xlsx";
      link.download = "attendance_report.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Demo Excel download error:", err);
    } finally {
      setLoadingOverlay(false);
    }
  };

  return (
    <Card>
    <div className="attendance-details-filters mb-4 shadow-md rounded-xl"
    style={{ marginBottom: 12 }}>
      {/* <div className="page-filters" style={{ marginBottom: 12 }}> */}
      <LoaderOverlay />
      <Row
        gutter={[16, 16]}
        align="middle"        
        style={{ alignItems: "center", marginBottom: 12 }}
      >
        {accessChecker("approve_attendance") && (
          <Col xs={24} sm={12} md={12} lg={8} xl={6}>
            <label>All Users</label>
            <SearchListAutoComplete
              optionsList={employees.map((c) => ({
                sale_id: c.id,
                display_text: c.user_name,
              }))}
              onSelect={(obj) =>
                handleFilterChange("employee_id", obj?.sale_id)
              }
              placeholder="Account"
              showAddNew={false}
              allowClear
            />
          </Col>
        )}

        {/* <RecordDateFilter
          filters={filters}
          handleFilterChange={handleFilterChange}
        /> */}
        
        <CommonDateRangeFilter
          filters={filters}
          onChange={(fromDate, toDate) => {
            handleFilterChange("fromDate", fromDate);
            handleFilterChange("toDate", toDate);
          }}
        />
        
        <Col xs={24} sm={12} md={12} lg={8} xl={6} style={{marginTop: "23px"}}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: screens.md ? 12 : 8,
              width: "100%",
            }}
          >
            <Button
              onClick={downloadAttendanceExcel}
              type="primary"
              size={screens.md ? "large" : "middle"}
              style={{
                flex: screens.md ? "0 0 auto" : "1 1 0",
                minWidth: 0,
                height: screens.md ? "36px" : "32px",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              Download Excel
            </Button>

            <Button
              onClick={attendanceReportDownload}
              type="primary"
              size={screens.md ? "large" : "middle"}
              style={{
                flex: screens.md ? "0 0 auto" : "1 1 0",
                minWidth: 0,
                height: screens.md ? "36px" : "32px",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              Download PDF
            </Button>
            <div style={{ flex: screens.md ? "0 0 auto" : "0 0 100%" }}>
              <Tooltip title="Refresh the list">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => dispatch(fetchAttendanceReport(filters))}
                  shape="circle"
                  size={screens.md ? "large" : "middle"}
                  title="Refresh"
                  style={{
                    minWidth: 0,
                    width: screens.md ? "36px" : "32px",
                    height: screens.md ? "36px" : "32px",
                  }}
                />
              </Tooltip>
            </div>
          </div>
        </Col>

      </Row>
      
      </div>

      <Table
        columns={columns}
        dataSource={attendanceReport}
        rowKey={(r) => r.id || r.attendance_id || Math.random()}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,

          showSizeChanger: true,
          showQuickJumper: true,

          pageSizeOptions: [ "10", "20", "50", "100"],

          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,

          onChange: (page, pageSize) => {
            setPagination({
              current: page,
              pageSize,
            });
          },

          onShowSizeChange: (current, size) => {
            setPagination({
              current: 1,
              pageSize: size,
            });
          },
        }}
        onChange={handleTableChange}
        bordered
        size={"middle"}
        scroll={{ x: "max-content" }}
      />
    </Card>
  );
};

export default AttendanceDetailsTab;
